import { Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { StatusManagementContext } from '../../models/job';
import { LogoutService } from '../../services/logout/logout.service';
import { Subscription } from 'rxjs';
import { Status } from '../../models/connector';
import { Endpoint, HelpIndex, MessageModalType, MessageType, ReviewSystem } from '../../models/enums';
import { EndUserLicenseAgreementModalComponent } from '../modals/end-user-license-agreement-modal/end-user-license-agreement-modal.component';
import { ObjectActionListType, Permissions } from '../../models/permission';
import { CommonService } from '../../services/common/common.service';
import { HttpService } from '../../services/http/http.service';
import { SettingsModalComponent } from '../modals/settings-modal/settings-modal.component';
import { SystemStatusModalComponent } from '../modals/system-status-modal/system-status-modal.component';
import { DownloadOCRUsageReportModalComponent } from '../modals/download-ocr-usage-report-modal/download-ocr-usage-report-modal.component';
import { AuditLogModalComponent } from '../modals/audit-log-modal/audit-log-modal.component';
import { AuditLog } from '../../models/audit-log';
import { RestService } from '../../services/rest/rest.service';
import { ReportType } from '../../models/report';
import { ReviewPromotionTrackingComponent } from '../status-management/reef-review-import-tracking/reef-review-promotion-tracking.component';
import { SelectAssociatedReviewProjectModalComponent } from '../modals/select-associated-review-project-modal/select-associated-review-project-modal.component';

@Component({
    standalone: false,
    selector: 'res-top-nav-bar',
    templateUrl: './top-nav-bar.component.html',
    styleUrls: ['./top-nav-bar.component.scss']
})

export class TopNavBarComponent implements OnInit, OnDestroy {
    @ViewChild('contentTemplate', { static: true }) contentTemplate: TemplateRef<any>;
    @ViewChild('openForReviewLink', { read: ElementRef, static: true }) reviewLink!: ElementRef;
    username: string;
    projectName: string;
    projectDescription: string;
    helpIndex: HelpIndex;
    currentYear = new Date().getFullYear();
    private sessionExpiredEvent: Subscription;
    settingsPermissions: boolean;
    statusPermissions: boolean;
    hasMonitoringAccess: boolean;
    statusIconClasses = 'fa-book';
    statusIconTooltip: string;
    statusIconAutoId: string;
    hasJobManagementPermission = false;
    hasJobHistoryPermission = false;
    hasSystemAuditLogPermission = false;
    hasActiveUserSessionsPermission = false;
    hasProjectAccessHistoryPermission = false;
    hasOCRUsageDownloadReportPermission = false;
    hasMonitoringPermission = false;
    updatePermissions: Subscription;
    hasReviewProjectId = false;
    isReefReviewProject = false;
    isRelativityProject = false;
    // Options to hide few section of the top nav bar
    @Input() isHomeButtonHidden = false;
    @Input() isProjectnameHidden = false;
    @Input() isRightMenuHidden = false;

    get reviewSystemIconTitle(): string {
        if (this.isReefReviewProject) return 'Reef ECA and Reef Review Project. Click to open the associated Reef Review project/s.';
        if (this.isRelativityProject) return 'Reef ECA and Relativity Server Project. Click to open the associated Relativity Server project/s.';
        return '';
    }

    constructor(private commonService: CommonService,
        private restService: RestService,
        private httpService: HttpService, private router: Router, private logoutService: LogoutService) {
        this.sessionExpiredEvent = this.commonService.getMessage(MessageType.sessionExpired).subscribe(() => {
            this.logoutService.handleSessionExpired();
        });
        this.updatePermissions = this.commonService.getMessage(MessageType.updatePermissions).subscribe(() => {
            this.setPermissions();
        });
    }

    ngOnInit(): void {
        this.username = this.commonService.currentUser.userName;
        this.setPermissions();
        if (this.commonService.currentProject && this.commonService.currentProject.name) {
            this.projectName = this.commonService.currentProject.name;
            this.hasReviewProjectId = this.commonService.currentProject?.reviewProjectId > 0;
            this.isReefReviewProject = this.commonService.currentProject?.reviewSystem === ReviewSystem.REEF_REVIEW;
            this.isRelativityProject = this.commonService.currentProject?.reviewSystem === ReviewSystem.RELATIVITY;
            // show settings button if user has at least one org/prj permission
            this.settingsPermissions = Permissions.hasOrgSettingsPermissions || Permissions.hasPrjSettingsPermissions;
            this.hasMonitoringAccess = Permissions.checkUserPermission(ObjectActionListType.PROJECT_MONITORING_ACCESS);
            if (this.hasMonitoringAccess && !this.commonService.documentViewerOpenContext) {
                this.loadProjectStatusInfo();
                this.httpService.startSystemStatusPoller();
            }
        }

        if (this.commonService.currentProject && this.commonService.currentProject.description) {
            this.projectDescription = this.commonService.currentProject.description;
        }
        this.helpIndex = this.projectName ? HelpIndex.ediscovery_review_workspace : HelpIndex.myedisc_sys_user_org_list_dash_dlg;
    }

    setPermissions(): void {
        this.hasJobManagementPermission = Permissions.checkUserPermission(ObjectActionListType.JOB_MANAGEMENT);
        this.hasJobHistoryPermission = Permissions.checkUserPermission(ObjectActionListType.JOB_AND_ERROR_HISTORY);
        this.hasActiveUserSessionsPermission = Permissions.checkUserPermission(ObjectActionListType.ACTIVE_USER_SESSIONS);
        this.hasOCRUsageDownloadReportPermission = Permissions.checkUserPermission(ObjectActionListType.OCR_USAGE_REPORT);
        this.hasProjectAccessHistoryPermission = Permissions.checkUserPermission(ObjectActionListType.PROJECT_ACCESS_LOG);
        this.hasMonitoringPermission = Permissions.checkUserPermission(ObjectActionListType.MONITORING);
        this.hasSystemAuditLogPermission = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_AUDIT_LOG);
        this.statusPermissions = this.isSystemUserWithAdminRights();
    }

    public isSystemUserWithAdminRights(): boolean {
        return Permissions.hasSysSettingsPermissions;
    }

    loadProjectStatusInfo(): void {
        if (this.hasMonitoringAccess) {
            this.httpService.post(Endpoint.projectManager_getProjectResourceStatus, { handle: this.commonService.currentProject.handle }, true).then((response) => {
                if (response) {
                    if (response.dataAreaStatus === Status.AVAILABLE && response.componentStatus === Status.AVAILABLE && response.storageStatus === Status.AVAILABLE) {
                        this.setDefaultStatusInfo();
                    } else if (response.dataAreaStatus === Status.FAILURE || response.componentStatus === Status.FAILURE || response.storageStatus === Status.FAILURE) {
                        this.statusIconClasses = 'fa-exclamation-circle color-red-04';
                        this.statusIconTooltip = 'System Status: Critical. Click for details.';
                        this.statusIconAutoId = 'critical-status-icon';
                    } else if (response.dataAreaStatus === Status.WARNING || response.componentStatus === Status.WARNING || response.storageStatus === Status.WARNING) {
                        this.statusIconClasses = 'fa-exclamation-triangle color-tan-06';
                        this.statusIconTooltip = 'System Status: Warning. Click for details.';
                        this.statusIconAutoId = 'warning-status-icon';
                    }
                } else {
                    this.setDefaultStatusInfo();
                }
            }).catch(() => {
                this.setDefaultStatusInfo();
            });
        } else {
            this.setDefaultStatusInfo();
        }
    }

    setDefaultStatusInfo(): void {
        this.statusIconClasses = 'fa-book';
        this.statusIconTooltip = '';
        this.statusIconAutoId = 'available-status-icon';
    }

    logOut(): void {
        const doLogout = () => this.logoutService.logout(this.commonService.currentUser);

        const proceedWithLogout = () => {
            if (this.commonService.projectName) {
                const handle = this.commonService.projectName;
                this.commonService.projectName = '';
                this.restService.post(Endpoint.projectManager_closeProject, { handle }).then(() => {
                    doLogout();
                }).catch(() => {
                    doLogout();
                });
            } else {
                doLogout();
            }
        };

        if (this.commonService.mainContent && this.commonService.currentProject) {
            this.commonService.mainContent.preselectNodeCallback(() => proceedWithLogout());
        } else {
            proceedWithLogout();
        }
    }

    logOutOnAllDevices(): void {
        this.logoutService.logoutOnAllDevices(this.commonService.currentUser);
    }

    homePage(): void {
        if (this.router.url !== '/project-list') {
            if (this.commonService.documentViewerOpenContext && this.commonService.mainContent) {
                this.commonService.sendMessage(MessageType.closeDocumentViewer);
            } else if (this.commonService.mainContent) {
                this.commonService.mainContent.preselectNodeCallback(() => this.homePageFunc());
            }
        }
    }

    homePageFunc(): void {
        this.commonService.clearData(true);
        this.httpService.stopSystemStatusPoller();
        this.router.navigate(['project-list'], { skipLocationChange: true });
    }

    openSettings(type): void {
        const data = { openType: type, projectHandle: this.commonService?.currentProject?.handle, organizationName: this.commonService?.currentUser?.customerName };
        this.commonService.openModal(SettingsModalComponent, { modalData: data, class: 'vertical-percent-100 horizontal-percent-100', keyboard: false });
    }

    openSystemSettings(): void {
        const data = { isSystemSettingsHidden: false, isOrgSettingsHidden: !this.projectName };
        this.commonService.openModal(SettingsModalComponent, { modalData: data, class: 'vertical-percent-100 horizontal-percent-100', keyboard: false });
    }

    openStatusManagement(preselectedStatusType: string): void {
        const statusManagementContext = new StatusManagementContext({ preselectedStatusType: preselectedStatusType });
        if (preselectedStatusType !== 'OCR Usage Report') {
            this.commonService.openExternalStatusManagement(statusManagementContext);
        } else {
            this.commonService.openModal(DownloadOCRUsageReportModalComponent);
        }
    }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);

    openMetadataHelp = (): void => this.commonService.openHelp(HelpIndex.metadata_list_dlg);

    openAboutModal = (): void => {
        this.commonService.openCustomContentMessageModal(this.contentTemplate, MessageModalType.information, 'About Reef ECA', null, null, null, 'about-modal-container');
    }

    ngOnDestroy(): void {
        this.updatePermissions?.unsubscribe();
        this.sessionExpiredEvent?.unsubscribe();
    }

    openSystemStatus(event): void {
        if (this.statusIconClasses !== 'fa-book') {
            this.commonService.openModal(SystemStatusModalComponent);
            event.stopPropagation();
        }
    }

    openInReefReviewOrRelativityFromIcon(event): void {
        if ((this.isReefReviewProject || this.isRelativityProject) && this.hasReviewProjectId) {
            this.openInReefReviewOrRelativity();
            event.stopPropagation();
        }
    }

    navigateToPrivacyPolicy(): void {
        window.open(this.commonService.privacyPolicyUrl, '_blank');
    }

    openEndUserLicenseAgreementModal(): void {
        this.commonService.openModal(EndUserLicenseAgreementModalComponent);
    }

    openInReefReviewOrRelativity(): void {
        if (this.commonService.currentProject.reviewProjects?.length === 1) {
            this.openReviewProjectLink(this.commonService.currentProject.reviewProjectId);
        } else {
            this.commonService.openModal(SelectAssociatedReviewProjectModalComponent, {
                modalData: {
                    reviewProjects: this.commonService.currentProject.reviewProjects,
                    reviewSystem: this.isReefReviewProject ? ReviewSystem.REEF_REVIEW : ReviewSystem.RELATIVITY
                }
            }).then(result => {
                if (result?.modalResult && result.modalData) {
                    const project = result.modalData;
                    this.openReviewProjectLink(project.reviewProjectId);
                }
            });
        }
    }

    openReviewProjectLink(reviewProjectId: number): void {
        this.httpService.post(Endpoint.projectManager_getReviewProjectFolderURL, {
            reviewSystem: this.isReefReviewProject ? ReviewSystem.REEF_REVIEW : this.isRelativityProject ? ReviewSystem.RELATIVITY : '',
            reviewProjectId: reviewProjectId,
            datasetName: null
        }).then((response) => {
            if (response?.url) {
                this.reviewLink.nativeElement.href = response.url;
                this.reviewLink.nativeElement.click();
            }
        });
    }

    openRRPromotionTracker(): void {
        this.commonService.openModal(ReviewPromotionTrackingComponent, { modalData: {}, class: 'vertical-percent-100 horizontal-percent-100' }).then();
    }

    reefReviewViewAuditLog(): void {
        this.commonService.openModal(AuditLogModalComponent, { modalData: new AuditLog(), class: 'vertical-percent-100 horizontal-percent-100' }).then();
    }

    reefReviewGenerateBillingReport(): void {
        this.restService.post(Endpoint.projectManager_generateConsolidateBillingReport, {
            handle: this.commonService.projectData.handle
        }).then(response => {
            this.commonService.openInformationToastNotification('Generation of the billing report started. You can download the report from Work Basket panel.');
        });
    }

    reefReviewGenerateSourceTrackingReport(): void {
        this.restService.post(Endpoint.projectManager_generateDownloadFromWorkbasketReport, {
            projectHandle: this.commonService.contextHandle,
            reportType: ReportType.SOURCE_TRACKING_REPORT
        });
        this.commonService.openInformationToastNotification('Generation of the source tracking report started. You can download the report from Work Basket panel.');
    }

    reefReviewGenerateProcessingReport(): void {
        this.restService.post(Endpoint.projectManager_generateDownloadFromWorkbasketReport, {
            projectHandle: this.commonService.contextHandle,
            reportType: ReportType.DATA_PROCESSING_REPORT
        });
        this.commonService.openInformationToastNotification('Generation of the processing report started. You can download the report from Work Basket panel.');
    }
}
